/**
 * Condition language mirror of backend/internal/expr. Both implementations are
 * verified against shared/expr-conformance.json — keep semantics identical.
 */
export type Op =
  | 'equals' | 'not_equals' | 'includes' | 'not_empty' | 'empty'
  | 'gt' | 'gte' | 'lt' | 'lte' | 'in'

export type Condition =
  | { all: Condition[] }
  | { any: Condition[] }
  | { not: Condition }
  | { field: string; op: Op; value?: unknown }

export type Answers = Record<string, unknown>

function scalarString(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (Array.isArray(v)) return v.map(scalarString).join(',')
  return String(v)
}

function isEmpty(v: unknown): boolean {
  return v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)
}

// Same grammar as the Go mirror: plain decimals only (no hex/inf/nan/underscores).
const NUM_RE = /^[+-]?([0-9]+\.?[0-9]*|\.[0-9]+)([eE][+-]?[0-9]+)?$/

function toNum(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string') {
    const t = v.replace(/^[ \t\n\r]+|[ \t\n\r]+$/g, '')
    if (!NUM_RE.test(t)) return null
    const n = Number(t)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function leaf(c: { field: string; op: Op; value?: unknown }, a: Answers): boolean {
  const v = a[c.field]
  switch (c.op) {
    case 'empty': return isEmpty(v)
    case 'not_empty': return !isEmpty(v)
    case 'not_equals': return scalarString(v) !== scalarString(c.value)
  }
  if (isEmpty(v)) return false
  switch (c.op) {
    case 'equals': return scalarString(v) === scalarString(c.value)
    case 'includes': {
      const w = scalarString(c.value)
      if (Array.isArray(v)) return v.some((e) => scalarString(e) === w)
      return scalarString(v).includes(w)
    }
    case 'in':
      return Array.isArray(c.value) && c.value.some((e) => scalarString(e) === scalarString(v))
    case 'gt': case 'gte': case 'lt': case 'lte': {
      const x = toNum(v)
      const y = toNum(c.value)
      if (x === null || y === null) return false
      return c.op === 'gt' ? x > y : c.op === 'gte' ? x >= y : c.op === 'lt' ? x < y : x <= y
    }
  }
  return false
}

export function evaluate(c: Condition, a: Answers): boolean {
  if ('all' in c) return c.all.every((s) => evaluate(s, a))
  if ('any' in c) return c.any.some((s) => evaluate(s, a))
  if ('not' in c) return !evaluate(c.not, a)
  return leaf(c, a)
}

export const MAX_DEPTH = 32
export const MAX_NODES = 1000

const OPS = new Set<string>([
  'equals', 'not_equals', 'includes', 'not_empty', 'empty', 'gt', 'gte', 'lt', 'lte', 'in',
])
const KEYS = new Set(['all', 'any', 'not', 'field', 'op', 'value'])

/**
 * Structural validation of untrusted JSON, mirroring Go's Decode + Validate.
 * Returns an error message, or null when the tree is safe to evaluate.
 * Always call before evaluate() on anything that did not come from the editor.
 */
export function validate(c: unknown): string | null {
  let nodes = 0
  const walk = (n: unknown, depth: number): string | null => {
    if (depth > MAX_DEPTH) return `condition nested deeper than ${MAX_DEPTH}`
    if (++nodes > MAX_NODES) return `condition has more than ${MAX_NODES} nodes`
    if (typeof n !== 'object' || n === null || Array.isArray(n)) return 'condition node must be an object'
    const o = n as Record<string, unknown>
    for (const k of Object.keys(o)) if (!KEYS.has(k)) return `unknown condition key "${k}"`
    const hasAll = Array.isArray(o.all)
    const hasAny = Array.isArray(o.any)
    if ('all' in o && !hasAll) return '"all" must be an array'
    if ('any' in o && !hasAny) return '"any" must be an array'
    const kinds = Number(hasAll) + Number(hasAny) + Number('not' in o) + Number(typeof o.field === 'string' && o.field !== '')
    if (kinds !== 1) return 'condition node must set exactly one of all/any/not/field'
    if (!('field' in o) || o.field === '') {
      if ('op' in o || 'value' in o) return 'op/value are only valid on a field node'
      for (const s of (o.all as unknown[]) ?? []) { const e = walk(s, depth + 1); if (e) return e }
      for (const s of (o.any as unknown[]) ?? []) { const e = walk(s, depth + 1); if (e) return e }
      if ('not' in o) return walk(o.not, depth + 1)
      return null
    }
    if (typeof o.op !== 'string' || !OPS.has(o.op)) return `unknown op "${String(o.op)}" on field "${String(o.field)}"`
    if (o.op === 'in' && !Array.isArray(o.value)) return `op in on "${String(o.field)}" needs an array value`
    if (['gt', 'gte', 'lt', 'lte'].includes(o.op) && toNum(o.value) === null) {
      return `op ${o.op} on "${String(o.field)}" needs a numeric value`
    }
    return null
  }
  return walk(c, 1)
}
